/**
 * DeveloperKeysTab.jsx
 * 
 * Settings page tab for managing API keys.
 * Role-aware:
 * - Owner/Admin: Create keys directly + approve/reject pending requests
 * - Regular users: Request keys (approval workflow)
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  Key, 
  Copy, 
  Eye, 
  EyeOff, 
  Plus, 
  Pencil,
  Check,
  X,
  Package,
  Clock,
  ShieldCheck,
  AlertCircle,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { apiKeysApi } from '../../services/api';
import { cn } from '../../lib/utils';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { useAuth } from '../../contexts/AuthContext';

const DeveloperKeysTab = () => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const { user } = useAuth();
  
  const isAdmin = user?.teamRole === 'owner' || user?.role === 'admin';
  
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revealedKeys, setRevealedKeys] = useState({});
  const [editingKeyId, setEditingKeyId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [copiedKeyId, setCopiedKeyId] = useState(null);
  
  // Create/Request modal state
  const [showModal, setShowModal] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [keyName, setKeyName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Pending requests (admin only)
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [deletingKeyId, setDeletingKeyId] = useState(null);

  useEffect(() => {
    loadKeys();
    if (isAdmin) loadPendingRequests();
  }, []);

  const loadKeys = async () => {
    try {
      setLoading(true);
      const response = await apiKeysApi.getMyKeys();
      setKeys(response.data.keys || []);
    } catch (error) {
      console.error('Failed to load keys:', error);
      toast.error('Failed to load your API keys');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingRequests = async () => {
    try {
      setLoadingRequests(true);
      const response = await apiKeysApi.listKeyRequests();
      setPendingRequests(response.data.requests || []);
    } catch (error) {
      console.error('Failed to load pending requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  };

  const loadAvailableProducts = async () => {
    try {
      const response = await apiKeysApi.getAvailableProducts();
      setAvailableProducts(response.data.products || []);
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  };

  const toggleReveal = (keyId) => {
    setRevealedKeys(prev => ({ ...prev, [keyId]: !prev[keyId] }));
  };

  const copyKey = async (keyId, fullKey) => {
    try {
      await navigator.clipboard.writeText(fullKey);
      setCopiedKeyId(keyId);
      toast.success('Key copied to clipboard');
      setTimeout(() => setCopiedKeyId(null), 2000);
    } catch (error) {
      toast.error('Failed to copy key');
    }
  };

  const startEditing = (key) => {
    setEditingKeyId(key.key_id);
    setEditingName(key.name);
  };

  const cancelEditing = () => {
    setEditingKeyId(null);
    setEditingName('');
  };

  const saveKeyName = async (keyId) => {
    try {
      await apiKeysApi.updateMyKeyName(keyId, editingName);
      setKeys(prev => prev.map(k => 
        k.key_id === keyId ? { ...k, name: editingName } : k
      ));
      setEditingKeyId(null);
      toast.success('Key name updated');
    } catch (error) {
      toast.error('Failed to update key name');
    }
  };

  const handleDeleteKey = async (keyId) => {
    try {
      await apiKeysApi.deleteMyKey(keyId);
      setKeys(prev => prev.filter(k => k.key_id !== keyId));
      setDeletingKeyId(null);
      toast.success('API key revoked');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke key');
    }
  };

  const openModal = () => {
    loadAvailableProducts();
    setShowModal(true);
    setSelectedProduct('');
    setKeyName('');
  };

  const handleSubmit = async () => {
    if (!selectedProduct || !keyName.trim()) {
      toast.error('Please select a product and enter a name');
      return;
    }

    try {
      setSubmitting(true);
      if (isAdmin) {
        // Direct creation - no approval needed
        const response = await apiKeysApi.createKey(selectedProduct, keyName.trim());
        toast.success('API key created successfully!');
        // Reload keys to show the new one
        await loadKeys();
      } else {
        // Request workflow
        await apiKeysApi.requestKey(selectedProduct, keyName.trim());
        toast.success('Key request submitted! An admin will review it shortly.');
      }
      setShowModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isAdmin ? 'create' : 'request'} key`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      await apiKeysApi.approveRequest(requestId);
      toast.success('Key request approved and key issued');
      setPendingRequests(prev => prev.filter(r => r.request_id !== requestId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    }
  };

  const handleReject = async (requestId) => {
    try {
      await apiKeysApi.rejectRequest(requestId);
      toast.success('Key request rejected');
      setPendingRequests(prev => prev.filter(r => r.request_id !== requestId));
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className={cn("p-6 border", colors.bgCard, colors.borderPrimary)} data-testid="api-keys-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center", colors.bgActive)}>
              <Key className={cn("w-6 h-6", colors.textAccent)} />
            </div>
            <div>
              <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>API Keys</h3>
              <p className={cn("text-sm", colors.textSecondary)}>
                API keys for integrating with Claude Desktop and other tools
              </p>
            </div>
          </div>
          <Button 
            onClick={openModal}
            className="flex items-center gap-2"
            data-testid="create-key-btn"
          >
            <Plus className="w-4 h-4" />
            {isAdmin ? 'Create Key' : 'Request Key'}
          </Button>
        </div>
      </Card>

      {/* Pending Requests (Admin only) */}
      {isAdmin && pendingRequests.length > 0 && (
        <Card className={cn("border", colors.bgCard, colors.borderPrimary)} data-testid="pending-requests-section">
          <div className={cn("p-4 border-b flex items-center gap-2", colors.borderPrimary)}>
            <AlertCircle className="w-4 h-4 text-amber-500" />
            <h4 className={cn("font-medium", colors.textPrimary)}>
              Pending Key Requests ({pendingRequests.length})
            </h4>
          </div>
          <div className="divide-y">
            {pendingRequests.map((req) => (
              <div key={req.request_id} className="p-4 flex items-center justify-between" data-testid={`pending-request-${req.request_id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={cn("font-medium", colors.textPrimary)}>
                      {req.team_member_name}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      <Package className="w-3 h-3 mr-1" />
                      {req.product_name}
                    </Badge>
                  </div>
                  <p className={cn("text-sm mt-1", colors.textSecondary)}>
                    Key name: "{req.suggested_name}" &middot; {formatDate(req.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => handleReject(req.request_id)}
                    data-testid={`reject-request-${req.request_id}`}
                  >
                    <X className="w-3 h-3 mr-1" />
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleApprove(req.request_id)}
                    data-testid={`approve-request-${req.request_id}`}
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Approve
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Keys List */}
      {keys.length === 0 ? (
        <Card className={cn("p-8 border text-center", colors.bgCard, colors.borderPrimary)} data-testid="no-keys-card">
          <Key className={cn("w-12 h-12 mx-auto mb-4 opacity-50", colors.textSecondary)} />
          <h4 className={cn("font-medium mb-2", colors.textPrimary)}>No API Keys</h4>
          <p className={cn("text-sm mb-4", colors.textSecondary)}>
            {isAdmin
              ? "You don't have any API keys yet. Create one to get started."
              : "You don't have any API keys yet. Request one from your admin to get started."}
          </p>
          <Button onClick={openModal} variant="outline" data-testid="empty-state-create-btn">
            <Plus className="w-4 h-4 mr-2" />
            {isAdmin ? 'Create Your First Key' : 'Request Your First Key'}
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {keys.map((key) => (
            <Card 
              key={key.key_id} 
              className={cn("p-4 border", colors.bgCard, colors.borderPrimary)}
              data-testid={`key-card-${key.key_id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-3">
                  {/* Product and Name */}
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Package className="w-3 h-3" />
                      {key.product_name}
                    </Badge>
                    
                    {editingKeyId === key.key_id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          className="h-7 w-48"
                          autoFocus
                        />
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => saveKeyName(key.key_id)}>
                          <Check className="w-4 h-4 text-green-500" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={cancelEditing}>
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className={cn("font-medium", colors.textPrimary)}>{key.name}</span>
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => startEditing(key)}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Key Value */}
                  <div className={cn("flex items-center gap-2 p-3 rounded-lg font-mono text-sm", colors.bgSecondary)}>
                    <span className="flex-1 break-all">
                      {revealedKeys[key.key_id] && key.full_key
                        ? key.full_key
                        : key.key_preview || 'sr_****...****'}
                    </span>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => toggleReveal(key.key_id)} data-testid={`reveal-key-${key.key_id}`}>
                        {revealedKeys[key.key_id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => copyKey(key.key_id, key.full_key)} disabled={!key.full_key} data-testid={`copy-key-${key.key_id}`}>
                        {copiedKeyId === key.key_id ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* Metadata + Actions */}
                  <div className="flex items-center justify-between">
                    <div className={cn("flex items-center gap-4 text-xs", colors.textSecondary)}>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Issued: {formatDate(key.issued_at)}
                      </span>
                      {key.last_used_at && (
                        <span>Last used: {formatDate(key.last_used_at)}</span>
                      )}
                    </div>
                    {deletingKeyId === key.key_id ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-red-600">Revoke this key?</span>
                        <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => handleDeleteKey(key.key_id)} data-testid={`confirm-delete-${key.key_id}`}>
                          Yes, Revoke
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setDeletingKeyId(null)}>
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeletingKeyId(key.key_id)}
                        data-testid={`delete-key-${key.key_id}`}
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Revoke
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Request Key Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className={colors.bgCard}>
          <DialogHeader>
            <DialogTitle>{isAdmin ? 'Create API Key' : 'Request API Key'}</DialogTitle>
            <DialogDescription>
              {isAdmin
                ? 'Select a product and name your key. It will be created immediately.'
                : 'Select a product and suggest a name for your key. An admin will review and issue it.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Product</Label>
              <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                <SelectTrigger data-testid="select-product-modal">
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent>
                  {availableProducts.map((product) => (
                    <SelectItem key={product.product_id} value={product.product_id}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {availableProducts.length === 0 && (
                <p className={cn("text-xs", colors.textSecondary)}>
                  No products available. Create a product first.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Key Name</Label>
              <Input
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
                placeholder="e.g., MacBook Pro, Work Desktop"
                data-testid="key-name-input"
              />
              <p className={cn("text-xs", colors.textSecondary)}>
                Help identify which device or environment this key is for
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={submitting || !selectedProduct || !keyName.trim()}
              data-testid="submit-key-btn"
            >
              {submitting
                ? (isAdmin ? 'Creating...' : 'Requesting...')
                : (isAdmin ? 'Create Key' : 'Submit Request')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeveloperKeysTab;
